import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../shared/useNotifications';
import './AuthShared.css';

const JudgeAccessRequest = ({ onBackToHome, onBackToLogin }) => {
  const navigate = useNavigate();
  const { signUp, signOut } = useAuth();
  const { success: showSuccessNotification, error: showErrorNotification } = useNotifications();
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    address: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('El nombre es obligatorio');
      return false;
    }

    if (!formData.email.trim()) {
      setError('El correo es obligatorio');
      return false;
    }

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('Por favor ingresa un correo válido');
      return false;
    }

    if (!formData.password) {
      setError('La contraseña es obligatoria');
      return false;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const { data: authDataWithProfile, error: authErrorWithProfile } = await signUp(
        formData.email.trim(),
        formData.password,
        'judge',
        {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || '',
          address: formData.address.trim() || '',
          is_requested: true,
          is_active: false,
        }
      );

      if (authErrorWithProfile) {
        setError(authErrorWithProfile);
        setLoading(false);
        return;
      }

      const judgeData = authDataWithProfile?.profile;
      if (!judgeData) {
        setError('Error guardando la solicitud del juez. Intenta nuevamente.');
        setLoading(false);
        return;
      }

      showSuccessNotification('✅ Solicitud enviada. Espera la aprobación del administrador.');
      // Cerrar sesión: el juez está pendiente de aprobación
      try { await signOut(); } catch (_) {}
      setStep('success');
    } catch (err) {
      console.error('Error en solicitud de acceso:', err);
      setError('Error al procesar tu solicitud. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    onBackToLogin();
  };

  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-success">
            <CheckCircle size={48} />
            <h2>¡Solicitud Enviada!</h2>
            <p>Tu solicitud de acceso ha sido registrada. El administrador la revisará pronto.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>
              Una vez aprobada, podrás iniciar sesión con tus credenciales.
            </p>

            <button
              type="button"
              onClick={onBackToLogin}
              className="auth-btn-primary"
              style={{ marginTop: 16 }}
            >
              Volver al Ingreso
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button
          type="button"
          onClick={onBackToHome}
          className="auth-back"
          aria-label="Volver atrás"
        >
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="auth-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="auth-logo" onClick={() => navigate('/')} />
          <h2>Solicitar Acceso</h2>
          <p>Completa el formulario para solicitar acceso</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-group">
            <label htmlFor="name">Nombre Completo *</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Tu nombre completo"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="email">Correo Electrónico *</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="tu@correo.com"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="password">Contraseña *</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="passwordConfirm">Confirmar Contraseña *</label>
            <input
              id="passwordConfirm"
              type="password"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleInputChange}
              placeholder="Confirma tu contraseña"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="phone">Teléfono</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Tu número de teléfono (opcional)"
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="address">Dirección</label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Tu dirección (opcional)"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading}
          >
            {loading ? 'Enviando solicitud...' : 'Solicitar Acceso'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            ¿Ya tienes cuenta?{' '}
            <button
              type="button"
              onClick={handleBackToLogin}
            >
              Inicia sesión aquí
            </button>
          </p>
        </div>

        <p className="auth-copyright">
          © {new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default JudgeAccessRequest;
